import * as THREE from 'three'
import type { GSplatVector3Input } from '@/plugins/gsplat/effects/effectUtils'
import { createVector3 } from '@/plugins/gsplat/effects/effectUtils'

export interface GSplatParticleMotionBoundsInput {
  boundsMin?: GSplatVector3Input
  boundsMax?: GSplatVector3Input
}

export interface GSplatParticleMotionBounds {
  min: THREE.Vector3
  max: THREE.Vector3
  center: THREE.Vector3
}

const MIN_EXTENT = 1e-6

export function createGSplatParticleMotionBounds(
  input: GSplatParticleMotionBoundsInput,
  effectScale: number
): GSplatParticleMotionBounds {
  const safeScale = Number.isFinite(effectScale) && effectScale > 0 ? effectScale : 1
  const fallbackMin = new THREE.Vector3(-safeScale, -safeScale, -safeScale)
  const fallbackMax = new THREE.Vector3(safeScale, safeScale, safeScale)
  const min = createVector3(input.boundsMin, fallbackMin)
  const max = createVector3(input.boundsMax, fallbackMax)

  if (!isFiniteVector3(min) || !isFiniteVector3(max)) {
    min.copy(fallbackMin)
    max.copy(fallbackMax)
  }

  for (const axis of ['x', 'y', 'z'] as const) {
    if (max[axis] < min[axis]) {
      const previousMin = min[axis]
      min[axis] = max[axis]
      max[axis] = previousMin
    }
    if (max[axis] - min[axis] < MIN_EXTENT) {
      const center = (min[axis] + max[axis]) * 0.5
      min[axis] = center - safeScale * 0.5
      max[axis] = center + safeScale * 0.5
    }
  }

  return {
    min,
    max,
    center: min.clone().add(max).multiplyScalar(0.5),
  }
}

function isFiniteVector3(value: THREE.Vector3): boolean {
  return Number.isFinite(value.x) && Number.isFinite(value.y) && Number.isFinite(value.z)
}
